import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Grid,
  Box,
  CircularProgress,
  Alert,
  Divider
} from '@mui/material';
import { Send as SendIcon, Edit as EditIcon } from '@mui/icons-material';
import axios from '../utils/axiosConfig';
import { handleApiError } from '../utils/apiErrorHandler';
import { toast } from '../utils/toast';

interface PaymentReminderDialogProps {
  open: boolean;
  onClose: () => void;
  confirmationId: string;
  clubName: string;
  totalAmount: number;
  onReminderSent?: () => void;
}

interface PaymentReminderTemplateData {
  recipientEmail: string;
  ccEmail: string;
  subject: string;
  templateText: string;
  competitionName: string;
  clubName: string;
  contactName: string;
  totalAmount: number;
  daysUntilCompetition: number;
}

const PaymentReminderDialog: React.FC<PaymentReminderDialogProps> = ({
  open,
  onClose,
  confirmationId,
  clubName,
  totalAmount,
  onReminderSent
}) => {
  const [templateData, setTemplateData] = useState<PaymentReminderTemplateData | null>(null);
  const [emailBody, setEmailBody] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open && confirmationId) {
      fetchTemplate();
    }
  }, [open, confirmationId]);

  const fetchTemplate = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await axios.get(
        `/api/payment-reminders/${confirmationId}/payment-reminder-template`
      );
      
      setTemplateData(response.data);
      setEmailBody(response.data.templateText);
    } catch (error) {
      handleApiError(error, 'Fehler beim Laden der Zahlungserinnerungs-Vorlage.');
      setError('Die E-Mail-Vorlage konnte nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  };

  const handleSendReminder = async () => {
    if (!emailBody.trim()) {
      toast.error('Bitte geben Sie einen E-Mail-Text ein.');
      return;
    }

    try {
      setSendingEmail(true);
      
      await axios.post(
        `/api/payment-reminders/${confirmationId}/send-payment-reminder`,
        { emailBody }
      );
      
      toast.success('Zahlungserinnerung erfolgreich versendet!');
      onReminderSent?.();
      onClose();
    } catch (error) {
      handleApiError(error, 'Fehler beim Versenden der Zahlungserinnerung.');
    } finally {
      setSendingEmail(false);
    }
  };

  const handleClose = () => {
    setEditMode(false);
    setError(null);
    setTemplateData(null);
    setEmailBody('');
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '60vh' }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SendIcon color="primary" />
          <Typography variant="h6">
            Zahlungserinnerung versenden
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : templateData ? (
          <Box>
            {/* Email Info */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  An:
                </Typography>
                <Typography variant="body2">
                  {templateData.recipientEmail}
                </Typography>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  CC:
                </Typography>
                <Typography variant="body2">
                  {templateData.ccEmail}
                </Typography>
              </Grid>
              
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">
                  Betreff:
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {templateData.subject}
                </Typography>
              </Grid>
            </Grid>

            <Divider sx={{ mb: 3 }} />

            {/* Competition and Amount Info */}
            <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Wettkampf:
                  </Typography>
                  <Typography variant="body2">
                    {templateData.competitionName}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Verein:
                  </Typography>
                  <Typography variant="body2">
                    {templateData.clubName}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Offener Betrag:
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: 'error.main' }}>
                    {templateData.totalAmount.toFixed(2)} €
                  </Typography>
                </Grid>
              </Grid>
            </Box>

            {/* Email Body */}
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle2">
                  E-Mail-Text:
                </Typography>
                <Button
                  size="small"
                  startIcon={<EditIcon />}
                  onClick={() => setEditMode(!editMode)}
                  variant={editMode ? "contained" : "outlined"}
                >
                  {editMode ? 'Vorschau' : 'Bearbeiten'}
                </Button>
              </Box>

              {editMode ? (
                <TextField
                  fullWidth
                  multiline
                  minRows={12}
                  maxRows={20}
                  value={emailBody}
                  onChange={(e) => setEmailBody(e.target.value)}
                  variant="outlined"
                  placeholder="E-Mail-Text eingeben..."
                />
              ) : (
                <Box
                  sx={{
                    border: 1,
                    borderColor: 'grey.300',
                    borderRadius: 1,
                    p: 2,
                    bgcolor: 'grey.50',
                    minHeight: 300,
                    maxHeight: 400,
                    overflow: 'auto',
                    whiteSpace: 'pre-line',
                    fontFamily: 'monospace'
                  }}
                >
                  {emailBody}
                </Box>
              )}
            </Box>

            {/* Timing Info */}
            {templateData.daysUntilCompetition > 0 && (
              <Alert severity="info" sx={{ mt: 2 }}>
                Der Wettkampf findet in {templateData.daysUntilCompetition} Tagen statt.
              </Alert>
            )}
          </Box>
        ) : null}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button 
          onClick={handleClose} 
          color="inherit"
          disabled={sendingEmail}
        >
          Abbrechen
        </Button>
        
        <Button
          onClick={handleSendReminder}
          variant="contained"
          color="primary"
          disabled={loading || sendingEmail || !emailBody.trim()}
          startIcon={sendingEmail ? <CircularProgress size={16} /> : <SendIcon />}
        >
          {sendingEmail ? 'Wird gesendet...' : 'Erinnerung senden'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PaymentReminderDialog;